// External imports
import React from "react";
import { HiCheck } from "react-icons/hi";

// Internal imports
import {
    Deal,
    DIV,
    PricingCardItemCategoryList,
    PricingCardItemCategoryListContainer,
    PricingCardItemCategoryListItem,
    PricingCardItemContainer,
    PricingCardItemContentButton,
    PricingCardItemContentButtonContainer,
    PricingCardItemContentContainer,
    PricingCardItemContentDescription,
    PricingCardItemContentPrice,
    PricingCardItemContentPriceMonth,
    PricingCardItemContentTitle,
    PricingCardYearltPrice,
} from "./PricingCard.styles";
import { FaCheck, FaPlus } from "react-icons/fa";

/**
 * Props for the PricingCard component
 * @property liItems - Array of features/items to display in the pricing card
 * @property price - Price value to display
 * @property title - Title of the pricing plan
 * @property details - Description or details of the plan
 * @property duration - Time period for the price (e.g., "month", "year")
 */
interface PricingCardProps {
    liItems: string[];
    price: string;
    title: string;
    details: string;
    duration: string;
    deal: string;
    showDeal: boolean;
    yearlyPlan: string;
    yearlyText: string;
    id: string;
    buttonText: string;
}

/**
 * PricingCard component
 * Displays a pricing plan card with title, price, features, and action button
 *
 * @example
 * ```tsx
 * <PricingCard
 *   title="Basic Plan"
 *   price="19.99"
 *   details="Perfect for starters"
 *   duration="month"
 *   liItems={["Feature 1", "Feature 2"]}
 * />
 * ```
 */
const PricingCard: React.FC<PricingCardProps> = ({
    liItems,
    price,
    title,
    details,
    duration,
    deal,
    showDeal,
    yearlyPlan,
    yearlyText,
    id,
    buttonText,
}) => {
    return (
        <PricingCardItemContainer>
            {showDeal && <Deal>{deal}</Deal>}
            <PricingCardItemContentContainer>
                <PricingCardItemContentTitle>
                    {title}
                </PricingCardItemContentTitle>
                <PricingCardItemContentDescription>
                    {details}
                </PricingCardItemContentDescription>
                <DIV>
                    <PricingCardItemContentPrice>
                        £{price}
                        <PricingCardItemContentPriceMonth>
                            /{duration}
                        </PricingCardItemContentPriceMonth>
                    </PricingCardItemContentPrice>
                    <PricingCardYearltPrice>
                        {yearlyPlan} {yearlyText}
                    </PricingCardYearltPrice>
                </DIV>
                <PricingCardItemContentButtonContainer>
                    <PricingCardItemContentButton
                        href="https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ03BbH2Z0PQLj4GcZ3y5fBz_2DZGYtF2HQ6k6lSF6wF28yxXRMN130fQelY6M0-eweDqdX4Bv3G"
                        target="_blank"
                    >
                        {buttonText}
                    </PricingCardItemContentButton>
                </PricingCardItemContentButtonContainer>
            </PricingCardItemContentContainer>
            <PricingCardItemCategoryListContainer>
                <PricingCardItemCategoryList>
                    {liItems.map((item, index) => (
                        <PricingCardItemCategoryListItem
                            key={`feature-${index}-${item}`}
                        >
                            {id === "basic" ? (
                                <FaCheck className="check-icon" />
                            ) : index == 0 ? (
                                <FaCheck className="check-icon" />
                            ) : (
                                <FaPlus className="check-icon" />
                            )}
                            {item}
                        </PricingCardItemCategoryListItem>
                    ))}
                </PricingCardItemCategoryList>
            </PricingCardItemCategoryListContainer>
        </PricingCardItemContainer>
    );
};

export default PricingCard;
